import QRCode from "qrcode";
import { installStates } from "../storage/installer";
import {
  formatBytes,
  packTotalBytes,
  type PackDescriptor,
  type PackManifest,
} from "../storage/manifest";
import { createShareKey, sharingUrl } from "../sharing/crypto";
import { MapShareSender } from "../sharing/mapTransfer";
import type { PeerStatus } from "../sharing/peerLink";

interface ExportScreenOptions {
  manifest: PackManifest;
  onCancel: () => void;
}

export class ExportScreen {
  private readonly root = document.createElement("div");
  private available: PackDescriptor[] = [];
  private selectedId = "";
  private sender: MapShareSender | null = null;
  private shareUrl = "";
  private status: HTMLElement | null = null;
  private progress: HTMLProgressElement | null = null;
  private progressText: HTMLElement | null = null;
  private complete = false;

  constructor(private readonly options: ExportScreenOptions) {
    this.root.className = "setup";
  }

  get element(): HTMLElement {
    return this.root;
  }

  async start(): Promise<void> {
    const states = await installStates(this.options.manifest.packs);
    this.available = this.options.manifest.packs.filter(
      (pack) => !pack.required && states.get(pack.id) === "installed",
    );
    this.selectedId = this.available[0]?.id ?? "";
    this.renderSelection();
  }

  private renderSelection(): void {
    const inner = document.createElement("div");
    inner.className = "setup-inner";
    const header = document.createElement("header");
    const title = document.createElement("h1");
    title.textContent = "Share a map";
    const lede = document.createElement("p");
    lede.className = "lede";
    lede.textContent =
      "Choose one installed area. Klint also sends its required background map so the other device can use it immediately.";
    header.append(title, lede);
    inner.append(header);

    if (this.available.length) {
      const list = document.createElement("ul");
      list.className = "packs";
      for (const pack of this.available) {
        const item = document.createElement("li");
        item.className = "pack";
        const radio = document.createElement("input");
        radio.type = "radio";
        radio.name = "export-pack";
        radio.id = `export-${pack.id}`;
        radio.value = pack.id;
        radio.checked = pack.id === this.selectedId;
        radio.addEventListener("change", () => (this.selectedId = pack.id));
        const label = document.createElement("label");
        label.htmlFor = radio.id;
        const name = document.createElement("div");
        name.className = "pack-label";
        name.textContent = pack.label;
        const note = document.createElement("div");
        note.className = "pack-note";
        note.textContent = pack.description;
        label.append(name, note);
        const size = document.createElement("span");
        size.className = "pack-size";
        size.textContent = formatBytes(packTotalBytes(pack));
        item.append(radio, label, size);
        list.append(item);
      }
      inner.append(list);
    } else {
      const notice = document.createElement("p");
      notice.className = "notice";
      notice.dataset.tone = "warn";
      notice.textContent = "There are no installed maps to share.";
      inner.append(notice);
    }

    const actions = document.createElement("div");
    actions.className = "actions";
    const share = document.createElement("button");
    share.className = "button";
    share.type = "button";
    share.disabled = !this.selectedId;
    share.textContent = "Generate sharing QR code";
    share.addEventListener("click", () => void this.beginSharing());
    const cancel = document.createElement("button");
    cancel.className = "link";
    cancel.type = "button";
    cancel.textContent = "Back to map";
    cancel.addEventListener("click", () => this.options.onCancel());
    actions.append(share, cancel);
    inner.append(actions);
    this.root.replaceChildren(inner);
  }

  private async beginSharing(): Promise<void> {
    const selected = this.available.find((pack) => pack.id === this.selectedId);
    if (!selected) return;
    const dependencies = this.options.manifest.packs.filter((pack) => pack.required);
    const packs = [...dependencies, selected];
    const key = createShareKey();
    this.shareUrl = sharingUrl(key);
    this.renderQr(selected, packs);

    const canvas = this.root.querySelector<HTMLCanvasElement>(".share-qr");
    if (canvas) {
      await QRCode.toCanvas(canvas, this.shareUrl, {
        width: 280,
        margin: 1,
        color: { dark: "#071722", light: "#ffffff" },
        errorCorrectionLevel: "M",
      });
    }

    this.sender = new MapShareSender(key, packs, {
      onStatus: (status) => this.showPeerStatus(status),
      onProgress: (pack, received, total) => this.showProgress(pack, received, total),
      onError: (error) => this.showError(error),
      onComplete: () => this.showComplete(),
    });
    await this.sender.start().catch((error: unknown) =>
      this.showError(error instanceof Error ? error : new Error("Could not start sharing.")),
    );
  }

  private renderQr(selected: PackDescriptor, packs: PackDescriptor[]): void {
    const inner = document.createElement("div");
    inner.className = "setup-inner share-screen";
    const header = document.createElement("header");
    const title = document.createElement("h1");
    title.textContent = `Share ${selected.label}`;
    const lede = document.createElement("p");
    lede.className = "lede";
    lede.textContent = "Have the other user scan this code with their camera and open Klint.";
    header.append(title, lede);

    const qrBox = document.createElement("div");
    qrBox.className = "qr-box";
    const canvas = document.createElement("canvas");
    canvas.className = "share-qr";
    const copy = document.createElement("button");
    copy.type = "button";
    copy.className = "link";
    copy.textContent = "Copy sharing link";
    copy.addEventListener("click", async () => {
      try {
        await navigator.clipboard.writeText(this.shareUrl);
        copy.textContent = "Link copied";
      } catch {
        copy.textContent = "Could not copy — scan the code instead";
      }
    });
    qrBox.append(canvas, copy);

    this.status = document.createElement("p");
    this.status.className = "notice transfer-status";
    this.status.textContent = "Connecting to the signaling service…";

    const summary = document.createElement("div");
    summary.className = "storage";
    const total = packs.reduce((sum, pack) => sum + packTotalBytes(pack), 0);
    for (const [label, value] of [
      ["Map package", selected.label],
      ["Maximum transfer", formatBytes(total)],
    ]) {
      const row = document.createElement("div");
      row.className = "storage-row";
      const left = document.createElement("span");
      left.textContent = label;
      const right = document.createElement("span");
      right.textContent = value;
      row.append(left, right);
      summary.append(row);
    }
    this.progress = document.createElement("progress");
    this.progress.className = "transfer-progress";
    this.progress.max = 1;
    this.progress.value = 0;
    this.progressText = document.createElement("div");
    this.progressText.className = "pack-note";
    this.progressText.textContent = "Waiting for the other device";
    summary.append(this.progress, this.progressText);

    const actions = document.createElement("div");
    actions.className = "actions";
    const stop = document.createElement("button");
    stop.className = "button secondary";
    stop.type = "button";
    stop.textContent = "Stop sharing";
    stop.addEventListener("click", () => {
      this.sender?.stop();
      this.options.onCancel();
    });
    actions.append(stop);
    inner.append(header, qrBox, this.status, summary, actions);
    this.root.replaceChildren(inner);
  }

  private showPeerStatus(status: PeerStatus | "transferring" | "complete"): void {
    if (!this.status || this.complete || status === "transferring") return;
    const text: Record<PeerStatus | "complete", string> = {
      signaling: "Connecting to the signaling service…",
      waiting: "Waiting for the other device to scan the code…",
      connecting: "Peer found. Establishing a direct connection…",
      connected: "Connected. Preparing the map transfer…",
      disconnected: "The other device disconnected.",
      complete: "Map transfer complete.",
    };
    this.status.textContent = text[status];
  }

  private showProgress(pack: PackDescriptor, received: number, total: number): void {
    if (this.status) this.status.textContent = "Sending map data directly to the other device…";
    if (this.progress) this.progress.value = total ? received / total : 0;
    if (this.progressText) {
      this.progressText.textContent = `${pack.label}: ${formatBytes(received)} of ${formatBytes(total)}`;
    }
  }

  private showComplete(): void {
    this.complete = true;
    if (this.status) {
      this.status.dataset.tone = "success";
      this.status.textContent = "Map transfer complete. The other device has saved it offline.";
    }
    if (this.progress) this.progress.value = 1;
  }

  private showError(error: Error): void {
    if (!this.status) return;
    this.status.dataset.tone = "error";
    this.status.textContent = error.message;
  }
}
